import { FC, useMemo, useState } from 'react'
import { Affix, Button, Drawer, Stack, Text, TextInput, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Map as MapboxMap } from 'react-map-gl/mapbox'
import { useMantineColorScheme } from '@mantine/core'
import { TbList, TbMap } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import 'mapbox-gl/dist/mapbox-gl.css'
import { getAllPlanningAreasOptions } from '@/api/main'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import AreaPolygons from './AreaPolygons'
import { boundsFromPoints, planningAreasToFeatureCollection } from './geojsonBuilders'

type PlanningAreaSelectorProps = {
  onSelectArea: (areaId: string) => void
}

export const PlanningAreaSelector: FC<PlanningAreaSelectorProps> = ({ onSelectArea }) => {
  const { t } = useTranslation('routePlanner')
  const { colorScheme } = useMantineColorScheme()
  const { data: areas } = useSuspenseQuery(getAllPlanningAreasOptions())
  const [drawerOpen, drawerHandlers] = useDisclosure(true)
  const [search, setSearch] = useState('')
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  const sortedAreas = useMemo(() => [...areas].sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id)), [areas])

  const featureCollection = useMemo(() => planningAreasToFeatureCollection(sortedAreas), [sortedAreas])

  const filteredAreas = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sortedAreas
    return sortedAreas.filter(a => a.id.toLowerCase().includes(q) || (a.name ?? '').toLowerCase().includes(q))
  }, [sortedAreas, search])

  const initialViewState = useMemo(() => {
    const bounds = boundsFromPoints(sortedAreas.flatMap(a => a.bounds.points))
    if (bounds) {
      return { bounds, fitBoundsOptions: { padding: 60, maxZoom: 14 } }
    }
    return { zoom: 12, ...determineMapCenter() }
  }, [sortedAreas])

  const mapStyle = useMemo(
    () => `mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`,
    [colorScheme]
  )

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <MapboxMap
        mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
        mapStyle={mapStyle}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['area-polygons-fill']}
        onClick={event => {
          const feature = event.features?.[0]
          const areaId = feature?.properties?.areaId
          if (typeof areaId === 'string') onSelectArea(areaId)
        }}
        onMouseMove={event => {
          const feature = event.features?.[0]
          const areaId = feature?.properties?.areaId
          setHighlightedId(typeof areaId === 'string' ? areaId : null)
        }}
        cursor={highlightedId ? 'pointer' : 'grab'}
      >
        <AreaPolygons data={featureCollection} options={{ highlightedId }} />
      </MapboxMap>

      <Affix position={{ top: 16, left: 16 }}>
        <Button leftSection={<TbList size={16} />} onClick={drawerHandlers.open} variant="filled">
          {t('areas')}
        </Button>
      </Affix>

      <Drawer
        opened={drawerOpen}
        onClose={drawerHandlers.close}
        position="left"
        title={t('planningAreas')}
        size="sm"
        withOverlay={false}
      >
        <Stack gap="sm">
          <TextInput
            placeholder={t('searchAreasPlaceholder')}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            leftSection={<TbMap size={16} />}
          />
          {filteredAreas.map(area => (
            <UnstyledButton
              key={area.id}
              onClick={() => onSelectArea(area.id)}
              onMouseEnter={() => setHighlightedId(area.id)}
              onMouseLeave={() => setHighlightedId(null)}
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                background: highlightedId === area.id ? 'var(--mantine-color-green-1)' : undefined,
              }}
            >
              <Text fw={600} size="sm">
                {area.name ?? area.id}
              </Text>
              <Text size="xs" c="dimmed">
                {area.id}
              </Text>
            </UnstyledButton>
          ))}
        </Stack>
      </Drawer>
    </div>
  )
}

export default PlanningAreaSelector
